// =============================================================================
// SetPasswordBanner — soft reminder for passwordless (Shopify-provisioned)
// accounts to set a Portal password.
// =============================================================================
// Shown while `user.hasPassword === false`. Dismissible per browser via
// localStorage; reappears in a new browser/session until a password is set,
// then disappears permanently (server-driven flag).
// =============================================================================

import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '@/hooks/use-auth';

const DISMISS_KEY = 'tkpod.setPasswordBanner.dismissed';

export function SetPasswordBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );

  const needsPassword = user?.hasPassword === false;
  const onProfilePage = location.pathname.startsWith('/profile');

  if (!needsPassword || dismissed || onProfilePage) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3"
      role="status"
    >
      <svg
        className="mt-0.5 h-5 w-5 shrink-0 text-brand-600"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
        />
      </svg>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary">
          Secure your Portal account
        </p>
        <p className="mt-0.5 text-sm text-text-secondary">
          Your account was created through Shopify and has no password yet. Set
          one to sign in to the Portal directly, without going through Shopify.
        </p>
        <Link
          to="/profile#password"
          className="mt-1.5 inline-block text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          Set a password →
        </Link>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded-md p-1 text-text-tertiary transition-colors hover:bg-brand-100 hover:text-text-secondary"
        aria-label="Dismiss"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
